import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Popconfirm, Space } from 'antd'
import { PortalRichText } from '../../../models/PortalArtigoModel'
import { PortalRichTextEditModal } from './PortalRichTextEditModal'
import { PortalRichTextPreview } from './PortalRichTextPreview'

export function RichTextListEditor({
  label,
  addLabel = 'Adicionar',
  value,
  autoOpenToken,
  autoOpenIndex = 0,
  onAutoOpenHandled,
  onChange,
}: {
  label: string
  addLabel?: string
  value: PortalRichText[]
  autoOpenToken?: number
  autoOpenIndex?: number
  onAutoOpenHandled?: () => void
  onChange: (value: PortalRichText[]) => void
}) {
  const items = value || []
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const lastAutoOpenToken = useRef<number>()

  useEffect(() => {
    if (autoOpenToken === undefined || lastAutoOpenToken.current === autoOpenToken) return

    lastAutoOpenToken.current = autoOpenToken
    if (items[autoOpenIndex] !== undefined) setEditingIndex(autoOpenIndex)
    onAutoOpenHandled?.()
  }, [autoOpenIndex, autoOpenToken, items, onAutoOpenHandled])

  const updateItem = (index: number, item: PortalRichText) => {
    onChange(
      items.map((currentItem, currentIndex) => (currentIndex === index ? item : currentItem))
    )
  }
  const removeItem = (index: number) => {
    onChange(items.filter((_, currentIndex) => currentIndex !== index))
  }
  const duplicateItem = (index: number) => {
    const nextItems = [...items]
    nextItems.splice(index + 1, 0, cloneRichText(items[index]))
    onChange(nextItems)
  }
  const addItem = () => {
    onChange([...items, ''])
    setEditingIndex(items.length)
  }
  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= items.length) return

    const nextItems = [...items]
    const [item] = nextItems.splice(index, 1)
    nextItems.splice(targetIndex, 0, item)
    onChange(nextItems)
  }
  const editingItem = editingIndex === null ? undefined : items[editingIndex]
  const editTitle =
    editingIndex === null ? getRichTextEditTitle(label) : getRichTextEditTitle(label, editingIndex)

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <strong>{label}</strong>
      {items.map((item, index) => (
        <RichTextRow key={index}>
          <RichTextPreviewArea>
            <PortalRichTextPreview value={item} />
          </RichTextPreviewArea>
          <RichTextActions>
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditingIndex(index)}>
              Editar
            </Button>
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={index === 0}
              onClick={() => moveItem(index, -1)}
            />
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={index === items.length - 1}
              onClick={() => moveItem(index, 1)}
            />
            <Button size="small" icon={<CopyOutlined />} onClick={() => duplicateItem(index)}>
              Duplicar
            </Button>
            <Popconfirm
              title={`Remover ${label.toLowerCase()}`}
              description="Tem certeza que deseja remover este item?"
              okText="Remover"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeItem(index)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </RichTextActions>
        </RichTextRow>
      ))}
      <Button icon={<PlusOutlined />} onClick={addItem}>
        {addLabel}
      </Button>
      <PortalRichTextEditModal
        open={editingIndex !== null}
        title={editTitle}
        value={editingItem}
        onCancel={() => setEditingIndex(null)}
        onSave={(nextItem) => {
          if (editingIndex !== null) updateItem(editingIndex, nextItem || '')
          setEditingIndex(null)
        }}
      />
    </Space>
  )
}

export function RichTextField({
  label,
  value,
  autoOpenToken,
  onAutoOpenHandled,
  onChange,
}: {
  label: string
  value?: PortalRichText
  autoOpenToken?: number
  onAutoOpenHandled?: () => void
  onChange: (value?: PortalRichText) => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const lastAutoOpenToken = useRef<number>()

  useEffect(() => {
    if (autoOpenToken === undefined || lastAutoOpenToken.current === autoOpenToken) return

    lastAutoOpenToken.current = autoOpenToken
    setIsEditing(true)
    onAutoOpenHandled?.()
  }, [autoOpenToken, onAutoOpenHandled])

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <strong>{label}</strong>
      <RichTextRow>
        <RichTextPreviewArea>
          <PortalRichTextPreview value={value} />
        </RichTextPreviewArea>
        <RichTextActions>
          <Button size="small" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
            Editar
          </Button>
        </RichTextActions>
      </RichTextRow>
      <PortalRichTextEditModal
        open={isEditing}
        title={getRichTextEditTitle(label)}
        value={value}
        onCancel={() => setIsEditing(false)}
        onSave={(nextValue) => {
          onChange(nextValue)
          setIsEditing(false)
        }}
      />
    </Space>
  )
}

function cloneRichText(value: PortalRichText) {
  return JSON.parse(JSON.stringify(value)) as PortalRichText
}

function getRichTextEditTitle(label: string, index?: number) {
  const singularLabels: Record<string, string> = {
    Parágrafos: 'parágrafo',
    Marcadores: 'marcador',
    Resposta: 'resposta',
    Nota: 'nota',
    Legenda: 'legenda',
    'Descrição WhatsApp': 'descrição WhatsApp',
  }
  const singularLabel = singularLabels[label] || label.toLowerCase()
  const position = typeof index === 'number' ? ` ${index + 1}` : ''

  return `Editar ${singularLabel}${position}`
}

const RichTextRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 10px;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;

  .ant-input {
    flex: 1;
    width: 100%;
  }
`

const RichTextPreviewArea = styled.div`
  flex: 1;
  min-width: 0;
`

const RichTextActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
`
